import { useHeaderHeight } from '@react-navigation/elements';
import { useFocusEffect } from '@react-navigation/native';
import { Box, Button, HStack, KeyboardAvoidingView, Text, VStack } from 'native-base';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, Platform, TextInput as RNTextInput } from 'react-native';

import AuthTitle from 'app/components/AuthTitle';
import { MyCitypassLogo } from 'app/components/icons';
import Screen from 'app/components/Screen';
import TextInput from 'app/components/TextInput';
import TextLink from 'app/components/TextLink';
import useStore from 'app/hooks/useStore';
import { AuthenticationStackParamList } from 'app/navigation/AuthenticationStackNavigator';
import { useAuthenticationNav } from 'app/navigation/AuthenticationStackNavigator/AuthenticationNavProvider';
import { RootScreenProps } from 'app/navigation/RootNavigator';
import { useI18n } from 'app/providers/I18nProvider';
import LanguageSelect from 'app/screens/Authentication/LanguageSelect';
import { tID } from 'app/services/TestHelper';
import { isEmail } from 'app/utils/validation';

const MODULE = '[AuthenticationEnterEmailScreen]';

export type AuthenticationStepEnterEmailScreenProps = RootScreenProps<
  AuthenticationStackParamList,
  'AuthenticationStepEnterEmail'
>;

export default function AuthenticationEnterEmailScreen({
  navigation,
  route,
}: AuthenticationStepEnterEmailScreenProps) {
  const { t } = useI18n();
  const completeWelcome = useStore((s) => s.completeWelcome);
  const {
    email: storedEmail,
    handleVerifyEmail,
    handleExploreApp,
    verifyEmail,
  } = useAuthenticationNav();
  const headerHeight = useHeaderHeight();
  const emailRef = useRef<RNTextInput | null>(null);

  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [showLanguage, setShowLanguage] = useState(true);

  const handleSubmit = () => {
    if (typeof email === 'string' && isEmail(email)) {
      completeWelcome();
      handleVerifyEmail(email);
    } else {
      emailRef.current?.focus();
    }
  };

  const handleEmailChange = (value: string) => {
    const sanitizedValue = value.trim().toLowerCase();
    setIsValid(isEmail(sanitizedValue));
    setEmail(sanitizedValue);
  };

  useFocusEffect(
    useCallback(() => {
      if (storedEmail && !email) setEmail(storedEmail);
    }, [email, storedEmail])
  );

  useEffect(() => {
    const subscriptions = [
      Keyboard.addListener('keyboardWillShow', () => setShowLanguage(false)),
      Keyboard.addListener('keyboardWillHide', () => setShowLanguage(true)),
    ];

    return () => {
      subscriptions.forEach((sub) => sub.remove());
    };
  }, []);

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={headerHeight + 20}
      style={{ flex: 1 }}
    >
      <Screen
        after={showLanguage ? <LanguageSelect disabled={verifyEmail.isLoading} /> : null}
        flexGrow="1"
        keyboardShouldPersistTaps="handled"
        testID={tID('AuthenticationEnterEmailScreen')}
      >
        <VStack space="6">
          <Box alignItems="center" pb="7" pt="16">
            <MyCitypassLogo size={260} />
          </Box>

          <Box alignItems="center">
            <AuthTitle>{t('app_auth_email_title')}</AuthTitle>
            <Text textAlign="center">{t('app_auth_email_description')}</Text>
          </Box>

          <TextInput
            ref={emailRef}
            autoComplete="email"
            clearButtonMode="while-editing"
            isInvalid={!isValid}
            isReadOnly={verifyEmail.isLoading}
            isRequired
            keyboardType="email-address"
            label={t<string>('app_auth_email_input')}
            testID={tID('txt-email')}
            textContentType="emailAddress"
            type="text"
            value={email}
            variant="default"
            onChangeText={handleEmailChange}
            onSubmitEditing={handleSubmit}
          />

          <Button
            colorScheme="primary"
            isDisabled={!isValid}
            isLoading={verifyEmail.isLoading}
            onPress={handleSubmit}
          >
            {t('gen_continue')}
          </Button>

          <HStack
            alignItems="baseline"
            flexWrap="wrap"
            justifyContent="center"
            mt="5"
            space="1"
            testID={tID('txt-welcome-explore')}
          >
            <Text fontSize="xs" textAlign="center">
              {t('app_welcome_explore_label')}
            </Text>
            <TextLink
              disabled={verifyEmail.isLoading}
              fontSize="xs"
              testID={tID('txt-welcome-explore-cta')}
              onPress={handleExploreApp}
            >
              {t('app_welcome_explore_cta')}
            </TextLink>
          </HStack>
        </VStack>
      </Screen>
    </KeyboardAvoidingView>
  );
}
